import { requireNonNullable } from '@disy/lib/custom-error';
import { getDisyNamespace } from '@disy/lib/disy';

export type CadenzaUrl = string;

/*
 * Prefixes relative URLs with the context path and maintains the jsessionid param.
 *
 * See net/disy/cadenza/web/util/UrlEncodingUtilities.java
 */
export function cadenzaUrl(url: string): CadenzaUrl {
  if (!url) {
    return '';
  }

  const jsessionid = getJsessionidParam(location.href);
  if (isExternal(url) && jsessionid) {
    return removeCadenzaJsessionidParam(url, jsessionid);
  }

  if (!hasScheme(url) && !matchesContextPath(url)) {
    return getDisyNamespace().contextPath + url;
  }

  return url;
}

/**
 * Returns true if the given URL includes an origin (i.e. it is not a relative URL)
 * and it's different from the current page's origin,
 * or if the origins match but the path of the given URL does not start with
 * the current Cadenza context path, as defined in the global Disy object.
 * Always returns false for relative URLs.
 * See the tests for examples.
 * @param urlStr The URL to check.
 */
export function isExternal(urlStr: string) {
  if (!hasScheme(urlStr)) {
    return false;
  }

  const url = new URL(urlStr);
  const currentOrigin = new URL(window.location.href).origin;
  return url.origin !== currentOrigin || !matchesContextPath(url.pathname);
}

function hasScheme(url: string) {
  return /^[a-z]+:/i.test(url);
}

function matchesContextPath(path: string) {
  const contextPath = requireNonNullable(getDisyNamespace().contextPath);
  return path === contextPath || path.startsWith(contextPath + '/');
}

function removeCadenzaJsessionidParam(url: string, jsessionid: string) {
  return url.replace(`;jsessionid=${jsessionid}`, '');
}

function getJsessionidParam(url: string) {
  return url.match(/;jsessionid=(\w+)/)?.[1];
}
